import { findRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { Recipe, RecipeWidget, WidgetState } from '@kemu-io/kemu-core/types';
import { safeJSONStringify } from '../utils';

/**
 * Represents the changes detected during execution mode.
 */
export type ExecutionModeChanges = {
  /** Whether any changes were detected */
  hasChanges: boolean;
  /** Number of widgets added during execution mode */
  addedWidgetsCount: number;
  /** Number of widgets deleted during execution mode */
  deletedWidgetsCount: number;
  /** Number of widgets with state, position, or connection changes */
  changedWidgetsCount: number;
};

/**
 * Extracts all widget IDs from a recipe in the format `thingId:widgetId`.
 * @param recipe - the recipe to extract widget IDs from.
 * @returns an array of composite widget IDs.
 */
const extractWidgetIdsFromRecipe = (recipe: Recipe): string[] => {
  const widgetIds: string[] = [];
  for (const thingId of Object.keys(recipe.blocks || {})) {
    for (const widgetId of Object.keys(recipe.blocks[thingId]?.gates || {})) {
      widgetIds.push(`${thingId}:${widgetId}`);
    }
  }
  return widgetIds;
};

/**
 * Retrieves the widget from a recipe.
 * @param recipe - the recipe to read from.
 * @param thingId - the id of the thing containing the widget.
 * @param widgetId - the id of the widget.
 * @returns the widget or null if not found.
 */
const getWidgetFromRecipe = (
  recipe: Recipe,
  thingId: string,
  widgetId: string
): RecipeWidget | null => {
  const block = recipe.blocks?.[thingId];
  if (!block) { return null; }

  const widget = block.gates?.[widgetId];
  if (!widget) { return null; }

  return widget;
};

/**
 * Checks if a widget has any changes (state, position, or connections).
 * @param currentWidget - the current widget.
 * @param snapshotWidget - the snapshot widget.
 * @returns true if the widget has any changes.
 */
const widgetHasChanges = (currentWidget: RecipeWidget, snapshotWidget: RecipeWidget): boolean => {
  // Check state changes (using preserveTripleDollar=true to include $$$-prefixed properties)
  const currentStateStr = safeJSONStringify(currentWidget.state, true);
  const snapshotStateStr = safeJSONStringify(snapshotWidget.state, true);
  if (currentStateStr !== snapshotStateStr) {
    return true;
  }

  // Check canvas (position) changes
  const currentCanvasStr = JSON.stringify(currentWidget.canvas || {});
  const snapshotCanvasStr = JSON.stringify(snapshotWidget.canvas || {});
  if (currentCanvasStr !== snapshotCanvasStr) {
    return true;
  }

  // Check connection (children) changes
  const currentChildrenStr = JSON.stringify(currentWidget.children || []);
  const snapshotChildrenStr = JSON.stringify(snapshotWidget.children || []);
  if (currentChildrenStr !== snapshotChildrenStr) {
    return true;
  }

  return false;
};

/**
 * Detects changes between the current recipe and the editing snapshot.
 * Checks for added widgets, deleted widgets, and widget state/position/connection changes.
 * @param recipePoolId - the id of the recipe in the pool.
 * @param editingSnapshot - the snapshot taken when entering execution mode.
 * @param editingModeWidgetIds - widget IDs that existed when entering execution mode.
 * @returns an object describing the detected changes.
 */
const detectExecutionModeChanges = (
  recipePoolId: string,
  editingSnapshot: Recipe | null,
  editingModeWidgetIds: string[] | null
): ExecutionModeChanges => {
  const noChanges: ExecutionModeChanges = {
    hasChanges: false,
    addedWidgetsCount: 0,
    deletedWidgetsCount: 0,
    changedWidgetsCount: 0,
  };

  // No snapshot means we cannot detect changes
  if (!editingSnapshot || !editingModeWidgetIds) {
    return noChanges;
  }

  const currentRecipe = findRecipe(recipePoolId);
  if (!currentRecipe) {
    return noChanges;
  }

  const currentWidgetIds = extractWidgetIdsFromRecipe(currentRecipe);
  const editingModeWidgetIdsSet = new Set(editingModeWidgetIds);
  const currentWidgetIdsSet = new Set(currentWidgetIds);

  // Detect added widgets (exist now but didn't exist in snapshot)
  const addedWidgets = currentWidgetIds.filter(id => !editingModeWidgetIdsSet.has(id));

  // Detect deleted widgets (existed in snapshot but don't exist now)
  const deletedWidgets = editingModeWidgetIds.filter(id => !currentWidgetIdsSet.has(id));

  // Detect changes for widgets that exist in both (state, position, or connection changes)
  let changedWidgetsCount = 0;
  for (const compositeId of editingModeWidgetIds) {
    // Skip if widget was deleted
    if (!currentWidgetIdsSet.has(compositeId)) { continue; }

    const [thingId, widgetId] = compositeId.split(':');
    
    const currentWidget = getWidgetFromRecipe(currentRecipe, thingId, widgetId);
    const snapshotWidget = getWidgetFromRecipe(editingSnapshot, thingId, widgetId);

    if (!currentWidget || !snapshotWidget) { continue; }

    if (widgetHasChanges(currentWidget, snapshotWidget)) {
      changedWidgetsCount++;
    }
  }

  const hasChanges = addedWidgets.length > 0 || deletedWidgets.length > 0 || changedWidgetsCount > 0;

  return {
    hasChanges,
    addedWidgetsCount: addedWidgets.length,
    deletedWidgetsCount: deletedWidgets.length,
    changedWidgetsCount,
  };
};

export default detectExecutionModeChanges;
